import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import Link from 'next/link'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import secureLink from '@utils/secureLink'
import { formatNameForDisplay } from '@utils/textDisplay'
import useOnScreen from '@hooks/useOnScreen'
import { gtmEvent } from '@lib/gtm-utils'
import CoachLogo from '@components/coach-logo'
import { getCoachHitFacility } from '@lib/algolia/coach-geoloc'
import useStyles, { CoachName } from '../leaderboard.style-hook'
import { MeetCoachBtn } from './pieces'

export function GetSkeletons({ isMobile, styles }) {
  return (
    <Box className={styles.outer} display="flex" justifyContent="space-around" alignItems="center" style={{ height: '88px' }}>
      {isMobile ? (
        <>
          <Skeleton width={200} height={80} />
          <Skeleton width={130} height={60} />
        </>
      ) : (
        <>
          <Skeleton width={112} height={35} variant="rect" />
          <Skeleton width={25} height={25} variant="circle" />
          <Skeleton width={300} height={35} variant="rect" />
          <Skeleton width={500} height={35} variant="rect" />
          <Skeleton width={130} height={50} variant="rect" />
        </>
      )}
    </Box>
  )
}

GetSkeletons.propTypes = {
  isMobile: PropTypes.bool,
  styles: PropTypes.object,
}

const defaultTitle = 'PGA Coach Near You'

const getCoachCityStateText = (coach) => {
  const facility = getCoachHitFacility(coach)
  const city = facility?.city || coach.city
  const state = facility?.state || coach.state
  if (city && state) {
    return `Your coach in ${city}, ${state}`
  }
  return defaultTitle
}

function CoachAdvertisment({
  coach, nativeAdDescription, nativeAdCta, leaderboardEvent,
}) {
  const styles = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'))
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const bannerAdRef = useRef(null)
  const isCardVisible = useOnScreen(bannerAdRef)
  const [bannerHasBeenViewed, setBannerHasBeenViewed] = useState(false)

  const coachName = coach?.firstName && coach?.lastName ? `${coach.firstName} ${coach.lastName}` : coach?.displayName

  useEffect(() => {
    if (!coach) return
    if (isCardVisible && !bannerHasBeenViewed) {
      gtmEvent({
        event: 'leaderboard-coach-ad-impression',
        attributes: {
          coach_name: coachName,
          coach_profile_id: coach.objectID,
          title: leaderboardEvent?.eventName,
          tour_name: leaderboardEvent?.tourName,
          member_type: coach?.memberType,
          certification_level: coach?.certificationLevel,
          has_plus_subscription: coach?.hasPlusSubscription,
        },
      })
      // Remove the event below in the future.
      gtmEvent({
        event: 'leaderboard_ad_impression',
        attributes: {
          bannerAdAction: 'impression',
          eventId: null,
          bannerAdType: 'coach',
          coachId: coach.objectID,
        },
      })
      setBannerHasBeenViewed(true)
    }
  }, [bannerHasBeenViewed, coach, coachName, isCardVisible, leaderboardEvent?.eventName, leaderboardEvent?.tourName])

  const handleBannerClick = () => {
    if (!coach) return
    gtmEvent({
      event: 'leaderboard-coach-ad-click',
      attributes: {
        coach_name: coachName,
        coach_profile_id: coach.objectID,
        event_name: leaderboardEvent?.eventName,
        tour_name: leaderboardEvent?.tourName,
        member_type: coach?.memberType,
        certification_level: coach?.certificationLevel,
        has_plus_subscription: coach?.hasPlusSubscription,
      },
    })
    // Remove the event below in the future.
    gtmEvent({
      event: 'leaderboard_ad_impression',
      attributes: {
        bannerAdAction: 'click',
        eventId: null,
        bannerAdType: 'coach',
        coachId: coach.objectID,
      },
    })
  }

  if (coach) {
    return (
      <div className={styles.outer} ref={bannerAdRef}>
        <Link href={`/coach/${coach.customSlug}`}>
          <Box onClick={() => handleBannerClick()}>
            <Typography className={styles.caption} variant="caption">
              {getCoachCityStateText(coach)}
            </Typography>
            <div className={styles.coachItem}>
              <Box display="flex" alignItems={isTablet ? 'center' : 'baseline'}>
                <Box ml={2}>
                  <CoachLogo
                    memberType={coach.memberType}
                    certificationLevel={coach.certificationLevel}
                    size={isMobile ? '40px' : '60px'}
                  />
                </Box>
                <div className={styles.info}>
                  {coach.profilePhoto ? (
                    <figure className={styles.roundImage}>
                      <img
                        src={secureLink(coach.profilePhoto)}
                        className={styles.coachAvatar}
                        alt="Coach Portrait"
                      />
                    </figure>
                  ) : null}
                  <CoachName coach={coach}>
                    <div className={styles.firstName}>{formatNameForDisplay(coach.firstName)}</div>
                    <Typography className={styles.lastName} variant="h6">{formatNameForDisplay(coach.lastName)}</Typography>
                  </CoachName>
                </div>
              </Box>
              {!isMobile && (
                <div className={styles.nativeDescription}>
                  <Typography variant="subtitle1">{nativeAdDescription}</Typography>
                </div>
              )}
              {isDesktop && <MeetCoachBtn styles={styles} nativeAdCta={nativeAdCta} />}
              {!isDesktop && <ArrowForwardIosIcon className={styles.carrotCta} />}
            </div>
          </Box>
        </Link>
      </div>
    )
  }
  return null
}

CoachAdvertisment.propTypes = {
  coach: PropTypes.object,
  nativeAdDescription: PropTypes.string,
  nativeAdCta: PropTypes.string,
  leaderboardEvent: PropTypes.shape({
    eventName: PropTypes.string,
    tourName: PropTypes.string,
  }),
}

export default CoachAdvertisment
